import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Spinner,
  Tab,
  TabList,
  Tabs,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React from 'react';

import { useGetDepartmentsQuery } from '@/API/departments.api';
import {
  useGetPersonnelViewMembershipAndAccessQuery,
  useUpdatePersonnelViewMembershipAndAccessMutation,
} from '@/API/personnel.api';
import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog';
import DepartmentsDropdown from '@/components/departments-dropdown/DepartmentsDropdown';
import DrawerActionButtons from '@/components/drawer-action-buttons/DrawerActionButtons';
import PersonnelViewAccess from '@/components/personnel-membership-drawer/PersonnelViewAccess';
import PersonnelViewMembership from '@/components/personnel-membership-drawer/PersonnelViewMembership';
import TemplatesDropdown from '@/components/templates-dropdown/TemplatesDropdown';
import { FEATURE_FLAGS_ENUM } from '@/constants/config';
import {
  DEFAULT_PERSONNEL_ID,
  DEFAULT_PERSONNEL_VIEW_MEMBERSHIP,
  DEFAULT_TOAST_DURATION,
  DEPARTMENTS_LOAD_ERROR_MESSAGE,
  ToastTypes,
} from '@/constants/defaults';
import { PersonnelViewMembershipAccessTabs } from '@/constants/enums';
import { DEFAULT_GAP, TOP_BAR_Z_INDEX, ZERO_SPACE } from '@/constants/ui';
import useDepartmentsRetryOnMountError from '@/hooks/useDepartmentsRetryOnMountError';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { makeSelectTemplatesForSelectedDepartments } from '@/store/slices/departmentsAndTemplates.slice';
import {
  initializePersonnelMembershipDrawerDraft,
  resetPersonnelMembershipDrawer,
  setPersonnelMembershipDrawerDepartmentListOpen,
  setPersonnelMembershipDrawerPersonnelData,
  setPersonnelMembershipDrawerSelectedDepartmentIds,
  setPersonnelMembershipDrawerSelectedTabIndex,
  setPersonnelMembershipDrawerSelectedTemplateIds,
  setPersonnelMembershipDrawerShowConfirmChangesDialog,
  setPersonnelMembershipDrawerTemplateListOpen,
} from '@/store/slices/personnelMembershipDrawer.slice';
import { RootState } from '@/store/store';
import { ApiError } from '@/types/api.types';
import { DepartmentSlim } from '@/types/department.types';
import { PersonnelViewAccessBody } from '@/types/personnel.types';

const selectedTabStyle = {
  background: 'blue.500',
  borderRadius: '10px',
  color: 'white',
};

const PERSONNEL_ACCESS_SUCCESS_MESSAGE = 'Personnel access updated successfully.';
const PERSONNEL_ACCESS_ERROR_MESSAGE = 'Error updating personnel access.';
const DEPARTMENTS_EXCLUDING_MRC_LOAD_ERROR_TOAST_ID = 'personnel-membership-departments-excluding-mrc-load-error';

const PersonnelMembershipDrawer = () => {
  const {
    isDepartmentListOpen,
    isLoading,
    isPersonnelMembershipDrawerOpen: isOpen,
    isTemplateListOpen,
    personnelData: { name },
    personnelViewAccessDraft,
    personnelViewAccessOriginal,
    selectedDepartmentIds,
    selectedPersonnelId: personnelId,
    selectedTabIndex,
    selectedTemplateIds,
    showConfirmChangesDialog,
  } = useAppSelector((state) => state.personnelMembershipDrawer);

  const { departments } = useAppSelector((state) => state.departmentsAndTemplates);

  const templateForSelectedDepartmentsSelector = React.useMemo(() => makeSelectTemplatesForSelectedDepartments(), []);

  const selectTemplatesForSelectedDepartments = useAppSelector((state: RootState) =>
    templateForSelectedDepartmentsSelector(state, selectedDepartmentIds),
  );

  const dispatch = useAppDispatch();
  const toast = useToast();
  const flags = useFlags();

  const isNoDepartmentMRCEnabled = flags[FEATURE_FLAGS_ENUM.LV15455NVMNoDepartmentMRC];

  /**
   * Like ViewAccessControls, this asks the endpoint to exclude MRC server-side rather than using the shared,
   * unfiltered departmentsAndTemplates.departments list. The dropdown this feeds sits above the View
   * Access/View Membership tabs, not inside either one, and both tabs read the department ids it sets
   * (PersonnelViewAccess, PersonnelViewMembership) - so this exclusion applies to both tabs, not just Access.
   * Skipped entirely while the flag is off so nothing changes for this drawer's existing behavior.
   */
  const {
    data: departmentsExcludingMRCData,
    isError: isDepartmentsExcludingMRCError,
    isFetching: isFetchingDepartmentsExcludingMRC,
    isUninitialized: isDepartmentsExcludingMRCUninitialized,
    refetch: refetchDepartmentsExcludingMRC,
  } = useGetDepartmentsQuery({ excludeMRC: true }, { skip: !isNoDepartmentMRCEnabled });

  useDepartmentsRetryOnMountError(
    isNoDepartmentMRCEnabled && isDepartmentsExcludingMRCError,
    refetchDepartmentsExcludingMRC,
    toast,
    DEPARTMENTS_EXCLUDING_MRC_LOAD_ERROR_TOAST_ID,
  );

  const departmentsExcludingMRC = React.useMemo<DepartmentSlim[]>(() => {
    if (!departmentsExcludingMRCData) return [];

    return departmentsExcludingMRCData.map((dept) => ({
      departmentId: dept.department_id,
      departmentName: dept.name,
    }));
  }, [departmentsExcludingMRCData]);

  const activeDepartments = isNoDepartmentMRCEnabled ? departmentsExcludingMRC : departments;

  // A query which has not started yet reports isFetching false. Count uninitialized as loading too so the
  // dropdown cannot flash enabled over an empty list.
  const isDepartmentsDropdownLoading =
    isLoading ||
    (isNoDepartmentMRCEnabled && (isDepartmentsExcludingMRCUninitialized || isFetchingDepartmentsExcludingMRC));

  const { data: personnelViewMembershipData } = useGetPersonnelViewMembershipAndAccessQuery(personnelId, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, isLoading, isFetching, isError, status }) => ({
      data: data ?? DEFAULT_PERSONNEL_VIEW_MEMBERSHIP,
      isError,
      isFetching,
      isLoading,
      status,
    }),
    skip: !personnelId || personnelId === DEFAULT_PERSONNEL_ID || !isOpen,
  });

  const [updatePersonnelViewAccess, {isLoading:isLoadingPersonnelViewAccess}] = useUpdatePersonnelViewMembershipAndAccessMutation();

  React.useEffect(() => {
    if (personnelViewMembershipData && personnelViewMembershipData.id !== DEFAULT_PERSONNEL_ID) {
      const personnelViewAccessDraftData: PersonnelViewAccessBody = {
        id: personnelViewMembershipData.id,
        viewFilterMap: personnelViewMembershipData.viewFilterMap,
        viewIds: personnelViewMembershipData.accessibleViews,
        viewMembershipIds: personnelViewMembershipData.memberOfViews,
      };

      dispatch(initializePersonnelMembershipDrawerDraft(personnelViewAccessDraftData));
      dispatch(setPersonnelMembershipDrawerPersonnelData(personnelViewMembershipData));
    }
  }, [dispatch, personnelViewMembershipData]);

  const prevSelectedDeptIds = React.useRef<number[]>(selectedDepartmentIds);
  const prevSelectedTemplateIds = React.useRef<number[]>(selectedTemplateIds);

  React.useEffect(() => {
    if (!isDepartmentListOpen) {
      prevSelectedDeptIds.current = selectedDepartmentIds;
    }
  }, [isDepartmentListOpen, selectedDepartmentIds]);

  React.useEffect(() => {
    if (!isTemplateListOpen) {
      prevSelectedTemplateIds.current = selectedTemplateIds;
    }
  }, [isTemplateListOpen, selectedTemplateIds]);

  const [departmentIdsDraft, setDepartmentIdsDraft] = React.useState<number[]>(selectedDepartmentIds);

  React.useEffect(() => {
    if (!isOpen) {
      setDepartmentIdsDraft(selectedDepartmentIds);
    }
  }, [selectedDepartmentIds, isOpen]);

  const handleUpdateEmployeeAccess = async () => {
    try {
      const personnelAccessResponse = await updatePersonnelViewAccess(personnelViewAccessDraft).unwrap();

      if (personnelAccessResponse?.updated) {
        toast({
          description: 'Personnel access updated successfully',
          duration: DEFAULT_TOAST_DURATION,
          isClosable: true,
          position: 'top',
          status: ToastTypes.SUCCESS,
          title: PERSONNEL_ACCESS_SUCCESS_MESSAGE,
        });
      }
    } catch (error) {
      // Cast unknown error type to ApiError
      const apiError = error as ApiError;
      const message = apiError?.data?.Message ?? PERSONNEL_ACCESS_ERROR_MESSAGE;

      toast({
        description: 'Error updating personnel access - Network Error',
        duration: DEFAULT_TOAST_DURATION,
        isClosable: true,
        position: 'top',
        status: ToastTypes.ERROR,
        title: message,
      });
    } finally {
      // Close the drawer
      dispatch(resetPersonnelMembershipDrawer());
    }
  };

  const isDirty = () => {
    return !_.isEqual(personnelViewAccessOriginal, personnelViewAccessDraft);
  };

  const handleClose = (bypassConfirm = false) => {
    if (isDirty() && !bypassConfirm) {
      dispatch(setPersonnelMembershipDrawerShowConfirmChangesDialog(true));
    } else {
      dispatch(resetPersonnelMembershipDrawer());
    }
  };

  const getTopBar = (): React.JSX.Element => {
    return (
      <VStack
        align={'left'}
        bg={'#fff'}
        gap={5}
        justify={'space-between'}
        justifyContent={'space-between'}
        mb={1}
        position={'sticky'}
        top={ZERO_SPACE}
        zIndex={TOP_BAR_Z_INDEX}
      >
        <Tabs
          index={selectedTabIndex}
          isLazy
          onChange={(index) => dispatch(setPersonnelMembershipDrawerSelectedTabIndex(index))}
          position={'relative'}
          variant={'unstyled'}
        >
          <HStack
            bg={'#fff'}
            gap={DEFAULT_GAP}
            justify={'space-between'}
            position={'sticky'}
            top={ZERO_SPACE}
            zIndex={TOP_BAR_Z_INDEX}
          >
            <TabList>
              <Tab _selected={selectedTabStyle}>View Access</Tab>
              <Tab _selected={selectedTabStyle}>View Membership</Tab>
            </TabList>
            <DrawerActionButtons isLoading={isLoadingPersonnelViewAccess} onSave={handleUpdateEmployeeAccess} onCancel={handleClose} />
          </HStack>
        </Tabs>
        <HStack spacing="24px" alignItems="flex-end">
          <DepartmentsDropdown
            departmentChangeHandler={(departmentIds: number[]) => setDepartmentIdsDraft(departmentIds)}
            departmentList={activeDepartments}
              departmentListCloseHandler={() => {
              dispatch(setPersonnelMembershipDrawerSelectedDepartmentIds(departmentIdsDraft));
              dispatch(setPersonnelMembershipDrawerDepartmentListOpen(false));
            }}
            departmentListOpenHandler={() => {
              dispatch(setPersonnelMembershipDrawerDepartmentListOpen(true));
            }}
            isLoading={isDepartmentsDropdownLoading}
            noOptionsMessage={isNoDepartmentMRCEnabled && isDepartmentsExcludingMRCError ? DEPARTMENTS_LOAD_ERROR_MESSAGE : undefined}
            selectedIds={departmentIdsDraft}
          />
          <TemplatesDropdown
            selectedIds={selectedTemplateIds}
            templateChangeHandler={(templateIds: number[]) =>
              dispatch(setPersonnelMembershipDrawerSelectedTemplateIds(templateIds))
            }
            templateList={selectTemplatesForSelectedDepartments}
            templateListCloseHandler={() => dispatch(setPersonnelMembershipDrawerTemplateListOpen(false))}
            templateListOpenHandler={() => dispatch(setPersonnelMembershipDrawerTemplateListOpen(true))}
          />
        </HStack>
      </VStack>
    );
  };

  const getDrawerBody = (): React.JSX.Element => {
    switch (selectedTabIndex) {
      case PersonnelViewMembershipAccessTabs.ACCESS:
        return <PersonnelViewAccess />;
      case PersonnelViewMembershipAccessTabs.MEMBERSHIP:
        return <PersonnelViewMembership />;
    }
  };

  const handleCloseWithoutSaving = () => {
    dispatch(setPersonnelMembershipDrawerShowConfirmChangesDialog(false));
    dispatch(resetPersonnelMembershipDrawer());
  };

  return (
    <Drawer isOpen={isOpen} placement={'right'} onClose={handleClose} returnFocusOnClose={false} size={'lg'}>
      <DrawerOverlay />
      <ConfirmationDialog
        closeButtonLabel="Close"
        closeConfirmDialog={() => dispatch(setPersonnelMembershipDrawerShowConfirmChangesDialog(false))}
        confirmButtonLabel="Save"
        description="Are you sure you want to close without saving?"
        handleClose={handleCloseWithoutSaving}
        handleConfirm={handleUpdateEmployeeAccess}
        isOpen={showConfirmChangesDialog}
        title="Confirm Close"
      />
      <DrawerContent maxW={'1300px'}>
        <DrawerHeader>{`Editing view ${_.lowerCase(PersonnelViewMembershipAccessTabs[selectedTabIndex])} for ${
          name.display
        }`}</DrawerHeader>
        <DrawerBody>
          <VStack align={'top'}>
            {getTopBar()}
            {isLoading ? <Spinner>Loading</Spinner> : getDrawerBody()}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default PersonnelMembershipDrawer;
